import React, { useEffect, useState, useMemo, useCallback } from 'react'
import { View, Text, StyleSheet, Modal, ScrollView } from 'react-native'
import {
  Button,
  BUTTON_TYPE,
  COLORS,
  COMMON_STYLES,
  DatePicker,
  RadioButton,
  SIZE,
  Spinner,
  TEXT_STYLES,
  TYPOGRAPHY,
  VARIANTS_STYLES,
} from '@dehaat/dds'
import FilterListItem from './FilterListItem'
import FilterHeader from './FilterHeader'
import FilterTab from './FilterTab'
import DateInputCard from '../DateInputCard'
import CalendarIcon from 'app/icons/CalendarIcon'
import fetchFilterData from 'app/apis/fetchFilterData'
import ToastAndroid from 'app/mocks/ToastAndroid'
import {
  buildFilteredFilterOptions,
  structureFilterValues as buildStructuredFilterValues,
} from 'app/utils/filterModalUtils'

export enum FORM_INPUT_TYPE {
  RADIO = 'radio',
  CHECK_BOX = 'checkbox',
  DATE_PICKER = 'date',
}

type FilterModalState = Record<string, any>

interface Props {
  appliedFilterData: FilterModalState
  onApplyFilters: (data: FilterModalState) => void
  onClose: () => void
  filterPage: 'vendor' | 'change_request' | 'approver'
}

const DATE_RANGE_LABELS = {
  FROM: 'fromDate',
  TO: 'toDate',
} as const

const DATE_FILTER_CONFIG = {
  key: 'date',
  type: FORM_INPUT_TYPE.DATE_PICKER,
  options: [
    { id: 'today', value: 'Today' },
    { id: 'last7', value: 'Last 7 Days' },
    { id: 'last30', value: 'Last 30 Days' },
    { id: 'last365', value: 'Last 365 Days' },
    { id: 'custom', value: 'Custom Range' },
  ],
} as const

const FilterModal = ({
  filterPage,
  onClose,
  appliedFilterData,
  onApplyFilters,
}: Props) => {
  const [filterList, setFilterList] = useState<any[]>([])
  const [tabIndex, setTabIndex] = useState(0)
  const [appliedFilterState, setAppliedFilterState] =
    useState<FilterModalState>(appliedFilterData)
  const [isCalendarModalVisible, setIsCalendarModalVisible] = useState(false)
  const [activeDateField, setActiveDateField] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [dateValidationError, setDateValidationError] = useState('')

  const parseISODate = (value?: string) => {
    if (!value) return null
    const date = new Date(`${value}T00:00:00`)
    return Number.isNaN(date.getTime()) ? null : date
  }

  const getTodayStart = () => {
    const today = new Date()
    return new Date(today.getFullYear(), today.getMonth(), today.getDate())
  }

  useEffect(() => {
    const getFilterData = async () => {
      try {
        const response = await fetchFilterData(filterPage)
        if (response && 'filters' in response) {
          setFilterList((response as any).filters)
        }
      } catch (error) {
        console.error('Error fetching filter data:', error)
      } finally {
        setIsLoading(false)
      }
    }

    getFilterData()
  }, [filterPage])

  useEffect(() => {
    const normalizeAppliedData = (data: FilterModalState) => {
      const normalized: FilterModalState = {}
      Object.entries(data).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          if (value.length > 0 && typeof value[0] === 'object') {
            normalized[key] = value.map(v => String(v.id))
          } else {
            normalized[key] = value.map(String)
          }
        } else {
          normalized[key] = String(value)
        }
      })
      return normalized
    }

    setAppliedFilterState(normalizeAppliedData(appliedFilterData))
  }, [appliedFilterData])

  const currentFilter = filterList[tabIndex] || {}
  const { type: inputType, key: filterJsonKey } = currentFilter

  const filterKey = String(filterJsonKey || '')
    .toLowerCase()
    .replace(/\s+/g, '_')

  const filteredData = useMemo(
    () => buildFilteredFilterOptions(currentFilter, filterKey, appliedFilterState),
    [currentFilter, filterKey, appliedFilterState],
  )

  const itemId = (item: any) => {
    if (item == null) return ''
    if (typeof item === 'string' || typeof item === 'number')
      return String(item)
    if (item.id !== undefined) return String(item.id)
    if (item.value !== undefined) return String(item.value)
    if (item.name !== undefined) return String(item.name)
    return JSON.stringify(item)
  }
  const itemLabel = (item: any) => {
    if (item == null) return ''
    if (typeof item === 'string' || typeof item === 'number')
      return String(item)
    return item.name ?? item.label ?? String(item)
  }

  const isItemSelected = useCallback(
    (id: string) => {
      const key = filterKey
      if (!key) return false
      if (inputType === FORM_INPUT_TYPE.CHECK_BOX) {
        const current = (appliedFilterState[key] || []).map(String)
        return current.includes(String(id))
      }
      if (
        inputType === FORM_INPUT_TYPE.RADIO ||
        inputType === FORM_INPUT_TYPE.DATE_PICKER
      ) {
        return String(appliedFilterState[key]) === String(id)
      }
      return false
    },
    [inputType, filterKey, appliedFilterState],
  )

  const handleFilterItemChange = useCallback(
    (rawId: string) => {
      if (!filterKey) return

      const id = String(rawId)

      if (inputType === FORM_INPUT_TYPE.CHECK_BOX) {
        const prevArr = Array.isArray(appliedFilterState[filterKey])
          ? appliedFilterState[filterKey].map(String)
          : []

        const allIds = filteredData.map(d => itemId(d))

        if (id === 'ALL') {
          const allSelected =
            prevArr.length === allIds.length &&
            allIds.length > 0 &&
            allIds.every(a => prevArr.includes(a))
          const newValue = allSelected ? [] : Array.from(new Set(allIds))
          setAppliedFilterState(prev => ({ ...prev, [filterKey]: newValue }))
          return
        }

        const newValue = prevArr.includes(id)
          ? prevArr.filter((i: string) => i !== id)
          : [...prevArr, id]

        setAppliedFilterState(prev => ({ ...prev, [filterKey]: newValue }))
      } else if (
        inputType === FORM_INPUT_TYPE.RADIO ||
        inputType === FORM_INPUT_TYPE.DATE_PICKER
      ) {
        setAppliedFilterState(prev => ({ ...prev, [filterKey]: id }))
      }
    },
    [inputType, filterKey, appliedFilterState, filteredData],
  )

  const handleClearAll = useCallback(() => {
    setAppliedFilterState({})
  }, [])

  const structureFilterValues = useCallback(
    () => buildStructuredFilterValues(appliedFilterState, filterList),
    [appliedFilterState, filterList],
  )

  const handleApplyFilters = useCallback(() => {
    if (String(appliedFilterState.date) === 'custom') {
      const fromDate = parseISODate(appliedFilterState.fromDate)
      const toDate = parseISODate(appliedFilterState.toDate)
      const todayStart = getTodayStart()

      if ((fromDate && fromDate > todayStart) || (toDate && toDate > todayStart)) {
        setDateValidationError('Future date cannot be selected.')
        ToastAndroid.show('Future date cannot be selected', ToastAndroid.SHORT)
        return
      }

      if (fromDate && toDate && fromDate > toDate) {
        setDateValidationError('From Date cannot be after To Date.')
        ToastAndroid.show(
          'From Date cannot be after To Date',
          ToastAndroid.SHORT,
        )
        return
      }
    }

    setDateValidationError('')
    const structuredFilters = structureFilterValues()
    onApplyFilters(structuredFilters)
  }, [appliedFilterState, structureFilterValues, onApplyFilters])

  const handleDateSelection = useCallback(
    (date: string) => {
      if (!date || typeof date !== 'string') {
        setIsCalendarModalVisible(false)
        setActiveDateField(null)
        return
      }
      const [day, month, year] = date.split('-')
      const formattedDate = `${year}-${month}-${day}`
      const selectedDate = parseISODate(formattedDate)
      const todayStart = getTodayStart()

      if (!selectedDate) {
        setDateValidationError('Invalid date selected.')
        ToastAndroid.show('Invalid date selected', ToastAndroid.SHORT)
        return
      }

      if (selectedDate > todayStart) {
        setDateValidationError('Future date cannot be selected.')
        ToastAndroid.show('Future date cannot be selected', ToastAndroid.SHORT)
        return
      }

      const existingFromDate = parseISODate(appliedFilterState.fromDate)
      const existingToDate = parseISODate(appliedFilterState.toDate)

      if (
        activeDateField === DATE_RANGE_LABELS.FROM &&
        existingToDate &&
        selectedDate > existingToDate
      ) {
        setDateValidationError('From Date cannot be after To Date.')
        ToastAndroid.show('From Date cannot be after To Date', ToastAndroid.SHORT)
        return
      }

      if (
        activeDateField === DATE_RANGE_LABELS.TO &&
        existingFromDate &&
        selectedDate < existingFromDate
      ) {
        setDateValidationError('To Date cannot be before From Date.')
        ToastAndroid.show('To Date cannot be before From Date', ToastAndroid.SHORT)
        return
      }

      setDateValidationError('')
      setAppliedFilterState(prev => ({
        ...prev,
        [activeDateField === DATE_RANGE_LABELS.FROM ? 'fromDate' : 'toDate']:
          formattedDate,
      }))

      setIsCalendarModalVisible(false)
      setActiveDateField(null)
    },
    [activeDateField, appliedFilterState.fromDate, appliedFilterState.toDate],
  )

  const handleCloseCalendar = useCallback(() => {
    setIsCalendarModalVisible(false)
    setActiveDateField(null)
  }, [])

  const renderCheckboxFilter = () => {
    const key = filterKey

    if (
      key === 'node' &&
      (!appliedFilterState.state || appliedFilterState.state.length === 0)
    ) {
      return (
        <Text style={{ color: '#888', padding: 12 }}>
          Please select a state first
        </Text>
      )
    }

    const allIds = filteredData.map(d => itemId(d))
    const selectedIds = (appliedFilterState[key] || []).map(String)

    const allSelected =
      allIds.length > 0 &&
      selectedIds.length === allIds.length &&
      allIds.every(id => selectedIds.includes(id))

    return (
      <>
        <FilterListItem
          key="ALL"
          isSelected={allSelected}
          onChange={() => handleFilterItemChange('ALL')}
          filterId="ALL"
          filterName="Select All"
        />
        {filteredData.map((item: any) => {
          const id = itemId(item)
          const label = itemLabel(item)
          return (
            <FilterListItem
              key={`${filterKey}_${id}`}
              isSelected={isItemSelected(id)}
              onChange={() => handleFilterItemChange(id)}
              filterId={id}
              filterName={label}
            />
          )
        })}
      </>
    )
  }

  const renderRadioFilter = () => {
    const radioList = filteredData.map((item: any) => {
      const id = itemId(item)
      const label = itemLabel(item)
      const value = itemId(item)
      return {
        id,
        label,
        value,
        radioElement: <Text style={{ color: '#000' }}>{label}</Text>,
      }
    })

    return (
      <RadioButton
        containerStyle={{ gap: 8 }}
        list={radioList}
        onClick={index => {
          const selected = radioList[index]
          if (selected) handleFilterItemChange(selected.id)
        }}
        selected={radioList.findIndex(
          r => String(r.id) === String(appliedFilterState[filterKey]),
        )}
      />
    )
  }

  // Render date picker filter
  const renderDateFilter = () => {
    const dateKey = filterKey || DATE_FILTER_CONFIG.key
    const currentDateSelection = appliedFilterState[dateKey]

    return (
      <>
        <RadioButton
          containerStyle={{ gap: 8 }}
          list={DATE_FILTER_CONFIG.options.map(d => ({
            id: d.id,
            label: d.value,
            value: d.id,
            radioElement: <Text style={{ color: '#000' }}>{d.value}</Text>,
          }))}
          onClick={index => {
            const selectedItem = DATE_FILTER_CONFIG.options[index]
            if (selectedItem) handleFilterItemChange(selectedItem.id)
          }}
          selected={DATE_FILTER_CONFIG.options.findIndex(
            d => String(d.id) === String(currentDateSelection),
          )}
        />

        {String(currentDateSelection) === 'custom' && (
          <View style={{ gap: 12, marginTop: 12 }}>
            <DateInputCard
              label="From Date"
              value={appliedFilterState.fromDate}
              onPress={() => {
                setActiveDateField(DATE_RANGE_LABELS.FROM)
                setDateValidationError('')
                setIsCalendarModalVisible(true)
              }}
              icon={<CalendarIcon />}
            />
            <DateInputCard
              label="To Date"
              value={appliedFilterState.toDate}
              onPress={() => {
                setActiveDateField(DATE_RANGE_LABELS.TO)
                setDateValidationError('')
                setIsCalendarModalVisible(true)
              }}
              icon={<CalendarIcon />}
            />
            {dateValidationError ? (
              <Text style={styles.dateValidationErrorText}>{dateValidationError}</Text>
            ) : null}
          </View>
        )}
      </>
    )
  }

  const renderFilterContent = () => {
    switch (inputType) {
      case FORM_INPUT_TYPE.CHECK_BOX:
        return renderCheckboxFilter()
      case FORM_INPUT_TYPE.RADIO:
        return renderRadioFilter()
      case FORM_INPUT_TYPE.DATE_PICKER:
        return renderDateFilter()
      default:
        return null
    }
  }

  if (isLoading) {
    return (
      <Modal visible={true} onRequestClose={onClose}>
        <View style={styles.modalContainer}>
          <FilterHeader onClose={onClose} onClearAll={handleClearAll} />
          <View
            style={[
              COMMON_STYLES.flex1,
              COMMON_STYLES.justifyCenter,
              COMMON_STYLES.alignItemsCenter,
            ]}
          >
            <Spinner />
          </View>
        </View>
      </Modal>
    )
  }

  if (!filterList?.length) return null

  return (
    <Modal visible={true} onRequestClose={onClose}>
      <View style={styles.modalContainer}>
        <FilterHeader onClose={onClose} onClearAll={handleClearAll} />

        <View style={[COMMON_STYLES.row, styles.contentContainer]}>
          {/* Left Tabs */}
          <View style={styles.tabsContainer}>
            <ScrollView>
              {filterList.map((item, index) => (
                <FilterTab
                  key={item.key}
                  label={item.key}
                  isHighlighted={tabIndex === index}
                  onClick={() => setTabIndex(index)}
                  filterCount={0}
                />
              ))}
            </ScrollView>
          </View>

          <View style={styles.filterContentContainer}>
            <ScrollView style={styles.scrollContainer}>
              <View style={styles.filterOptionsContainer}>
                {renderFilterContent()}
              </View>
            </ScrollView>
          </View>
        </View>

        <View style={styles.stickyFooter}>
          <Button
            buttonStyle={styles.footerButton}
            text="Close"
            onClick={onClose}
            type={BUTTON_TYPE.TERTIARY}
            size={SIZE.large}
          />
          <Button
            buttonStyle={styles.footerButton}
            text="Apply Filter"
            onClick={handleApplyFilters}
            size={SIZE.large}
          />
        </View>
      </View>

      <DatePicker
        visible={isCalendarModalVisible}
        onClose={handleCloseCalendar}
        onConfirm={handleDateSelection}
      />
    </Modal>
  )
}

const styles = StyleSheet.create({
  modalContainer: {
    flex: 1,
    backgroundColor: COLORS.white,
  },
  contentContainer: {
    flex: 1,
    ...COMMON_STYLES.fullHeight,
  },
  tabsContainer: {
    width: 140,
    ...COMMON_STYLES.verticalDivider,
  },
  filterContentContainer: {
    ...COMMON_STYLES.flex1,
    ...COMMON_STYLES.p1,
  },
  filterTitle: {
    ...TEXT_STYLES.textNeutral90,
    ...VARIANTS_STYLES.MEDIUM,
    ...TYPOGRAPHY.TEXT1,
    marginBottom: 16,
  },
  scrollContainer: {
    flex: 1,
  },
  filterOptionsContainer: {
    ...COMMON_STYLES.pb5,
  },
  stickyFooter: {
    ...COMMON_STYLES.row,
    ...COMMON_STYLES.justifyBetween,
    padding: 16,
    backgroundColor: COLORS.white,
    borderTopWidth: 1,
    borderTopColor: COLORS.neutral20,
    shadowColor: COLORS.black,
    shadowOffset: {
      width: 0,
      height: -2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 5,
  },
  footerButton: {
    flex: 0.48,
    ...COMMON_STYLES.borderNone,
  },
  dateValidationErrorText: {
    ...TYPOGRAPHY.CAPTION1,
    ...TEXT_STYLES.textError100,
  },
})

export default FilterModal
